import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { CircularProgress, Box } from '@mui/material';
import authService from '../services/authService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'super-admin' | 'admin' | 'lawyer' | 'client' | 'staff' | ('super-admin' | 'admin' | 'lawyer' | 'client' | 'staff')[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { user, loading } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  // Show loading while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!authService.isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    );
  }

  // Check role requirements
  if (requiredRole) {
    const userRole = user?.role?.name;
    let hasPermission = false;
    
    if (Array.isArray(requiredRole)) {
      // Multiple roles allowed
      hasPermission = requiredRole.includes(userRole as any);
    } else {
      // Single role required
      hasPermission = userRole === requiredRole;
    }
    
    if (!hasPermission) {
      // Redirect unauthorized users to appropriate dashboard
      const roleRedirects: Record<string, string> = {
        admin: '/',
        lawyer: '/',
        client: '/',
        staff: '/',
        'super-admin': '/',
      };

      return (
        <Navigate
          to={roleRedirects[user?.role?.name || ''] || '/'}
          replace
        />
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
